import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, EMPTY, merge, Observable, switchMap, from, exhaustMap } from 'rxjs';
import { ApiService } from './api/api.service';
import { AuthService } from './auth.service';
import { EventBusService, EventType } from './event-bus.service';
import { SignalRService } from './signalr.service';
import { MonitoringStatistics } from '../types/generated.types';
import { TenantsService } from './tenants.service';

@Injectable({ providedIn: 'root' })
export class MonitoringStatisticsService {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly tenantsService = inject(TenantsService);
  private readonly eventBusService = inject(EventBusService);
  private readonly signalRService = inject(SignalRService);
  
  private statisticsSubject = new BehaviorSubject<MonitoringStatistics>(null);
  private isRefreshing = false;

  get statistics$(): Observable<MonitoringStatistics> {
    return this.statisticsSubject.asObservable();
  }

  constructor() {
    merge(
      this.tenantsService.selectedTenantId$,
      this.eventBusService.on(EventType.OrganizationChanged),
    )
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => from(this.refresh())),
        catchError(error => {
          console.error('Refresh failed:', error);
          return EMPTY;
        }),
      )
      .subscribe();

    this.signalRService.monitoringDataUpdated$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap(() => from(this.refresh())),
        catchError(error => {
          console.error('SignalR refresh failed:', error);
          return EMPTY;
        }),
      )
      .subscribe();

    void this.refresh();
  }

  async refresh(): Promise<void> {
    if (!this.authService.isLoggedIn || this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    try {
      const statistics = await this.apiService.monitoring.getStatistics(this.tenantsService.selectedTenantId);
      this.statisticsSubject.next(statistics);
    } catch {
      // ignored
    } finally {
      this.isRefreshing = false;
    }
  }
}
